import { useCallback, useEffect, useState } from "react";

export interface ApprovalReviewRecord {
  id: string;
  operation: string;
  entityType: string;
  entityId: string;
  makerId: string;
  requiredPermission: string;
  requiredApprovals: number;
  reason: string;
  status: string;
  checkerIds: string[];
  createdAt: string;
  decidedAt?: string | null;
}

export interface ApprovalReviewViewProps {
  initialRequestId?: string;
  loadApproval: (requestId: string) => Promise<ApprovalReviewRecord>;
  reviewApproval: (
    requestId: string,
    decision: "APPROVE" | "REJECT",
    reason: string,
    idempotencyKey: string,
  ) => Promise<ApprovalReviewRecord>;
}

function commandKey() {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `approval-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
}

function messageFrom(error: unknown) {
  const candidate = error as {
    message?: string;
    response?: { data?: { message?: string; code?: string } };
  };
  return (
    candidate.response?.data?.message ||
    candidate.response?.data?.code?.replace(/_/g, " ") ||
    candidate.message ||
    "Unable to load the approval request"
  );
}

export function ApprovalReviewView({
  initialRequestId = "",
  loadApproval,
  reviewApproval,
}: ApprovalReviewViewProps) {
  const [requestId, setRequestId] = useState(initialRequestId);
  const [approval, setApproval] = useState<ApprovalReviewRecord | null>(null);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [deciding, setDeciding] = useState<"APPROVE" | "REJECT" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (id = requestId) => {
      const normalized = id.trim();
      if (!normalized) {
        setError("Enter the approval request ID supplied by the cashier.");
        return;
      }
      setLoading(true);
      setError(null);
      try {
        setApproval(await loadApproval(normalized));
        setRequestId(normalized);
      } catch (loadError) {
        setApproval(null);
        setError(messageFrom(loadError));
      } finally {
        setLoading(false);
      }
    },
    [loadApproval, requestId],
  );

  useEffect(() => {
    if (initialRequestId.trim()) void load(initialRequestId);
  }, [initialRequestId, load]);

  const decide = async (decision: "APPROVE" | "REJECT") => {
    if (!approval) return;
    if (reason.trim().length < 3) {
      setError("Enter a meaningful review reason.");
      return;
    }
    setDeciding(decision);
    setError(null);
    try {
      setApproval(
        await reviewApproval(
          approval.id,
          decision,
          reason.trim(),
          commandKey(),
        ),
      );
      setReason("");
    } catch (reviewError) {
      setError(messageFrom(reviewError));
    } finally {
      setDeciding(null);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <header>
        <h1 className="text-2xl font-bold text-gray-900">Approval Center</h1>
        <p className="mt-1 text-sm text-gray-600">
          Review sensitive actions as a manager. The requester cannot approve
          their own action.
        </p>
      </header>

      <form
        className="flex gap-3 rounded-xl bg-white p-5 shadow-sm"
        onSubmit={(event) => {
          event.preventDefault();
          void load();
        }}
      >
        <label className="flex-1 text-sm font-medium text-gray-700">
          Approval request ID
          <input
            aria-label="Approval request ID"
            value={requestId}
            onChange={(event) => setRequestId(event.target.value)}
            placeholder="Paste the request UUID"
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2.5 font-normal"
          />
        </label>
        <button
          type="submit"
          disabled={loading}
          className="self-end rounded-lg bg-blue-600 px-5 py-2.5 font-semibold text-white disabled:opacity-50"
        >
          {loading ? "Loading…" : "Load request"}
        </button>
      </form>

      {error && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {error}
        </div>
      )}

      {approval && (
        <section className="space-y-5 rounded-xl bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3 border-b pb-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                {approval.operation.replace(/_/g, " ")}
              </div>
              <div className="mt-1 font-mono text-sm text-gray-800">
                {approval.id}
              </div>
            </div>
            <span className="rounded-full bg-amber-100 px-3 py-1 text-sm font-semibold text-amber-900">
              {approval.status}
            </span>
          </div>

          <dl className="grid gap-4 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-gray-500">Requested action</dt>
              <dd className="font-medium text-gray-900">
                {approval.entityType.replace(/_/g, " ")} · {approval.entityId}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Required authority</dt>
              <dd className="font-medium text-gray-900">
                {approval.requiredPermission}
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-gray-500">Request reason</dt>
              <dd className="font-medium text-gray-900">{approval.reason}</dd>
            </div>
          </dl>

          {approval.status === "PENDING" ? (
            <div className="space-y-3 border-t pt-4">
              <label className="block text-sm font-medium text-gray-700">
                Manager review reason
                <textarea
                  aria-label="Manager review reason"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  rows={3}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 font-normal"
                  placeholder="Record why this return is approved or rejected"
                />
              </label>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => void decide("APPROVE")}
                  disabled={Boolean(deciding)}
                  className="rounded-lg bg-green-600 px-5 py-2.5 font-semibold text-white disabled:opacity-50"
                >
                  {deciding === "APPROVE" ? "Approving…" : "Approve request"}
                </button>
                <button
                  type="button"
                  onClick={() => void decide("REJECT")}
                  disabled={Boolean(deciding)}
                  className="rounded-lg bg-red-600 px-5 py-2.5 font-semibold text-white disabled:opacity-50"
                >
                  {deciding === "REJECT" ? "Rejecting…" : "Reject request"}
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
              This request is {approval.status.toLowerCase()} and cannot be
              decided again.
            </div>
          )}
        </section>
      )}
    </div>
  );
}
